import React from 'react';

const THEMES = [
  'Recruitment',
  'Retention',
  'Workplace Adjustments',
  'Awareness & Training',
  'Leadership Commitment',
  'Disclosure',
];

const LEVELS = [
  { key: 'committed', label: 'Committed', color: 'bg-slate-500' },
  { key: 'employer',  label: 'Employer',  color: 'bg-teal-500'  },
  { key: 'leader',    label: 'Leader',    color: 'bg-amber-500' },
];

// Derive per-theme, per-level scores from assessment answers + gaps
function buildThemeScores(assessments) {
  if (!assessments || assessments.length === 0) {
    return THEMES.map(theme => ({ theme, committed: 0, employer: 0, leader: 0 }));
  }

  const latest = assessments[0];
  const answers = latest.answers || {};
  const gaps = latest.gaps || [];

  return THEMES.map(theme => {
    const themeKey = theme.toLowerCase().split(' ')[0];
    const isGap = gaps.some(g => g.toLowerCase().includes(themeKey));

    // Filter answers by level prefix convention (l1_, l2_, l3_) or fall back to level scores
    const byLevel = { committed: 0, employer: 0, leader: 0 };
    const levelMap = { '1': 'committed', '2': 'employer', '3': 'leader' };

    let hasThemeAnswers = false;
    Object.entries(answers).forEach(([key, val]) => {
      const match = key.match(/^l([123])_(.+)/);
      if (!match) return;
      const [, lvl, rest] = match;
      if (!rest.toLowerCase().includes(themeKey)) return;
      hasThemeAnswers = true;
      const levelName = levelMap[lvl];
      const positive = val === 'yes' || val === true || (typeof val === 'number' && val >= 3);
      // accumulate counts; we'll normalise after
      byLevel[levelName] = (byLevel[levelName] || 0) + (positive ? 1 : 0);
    });

    if (!hasThemeAnswers) {
      // Fall back to overall level scores
      const base = latest.maturity_score || 0;
      const penalty = isGap ? 20 : 0;
      byLevel.committed = Math.min(Math.max(base - penalty + 10, 0), 100);
      byLevel.employer  = Math.min(Math.max((latest.level2_score ?? base) - penalty, 0), 100);
      byLevel.leader    = Math.min(Math.max((latest.level3_score ?? base) - penalty - 10, 0), 100);
    } else {
      // Convert counts to rough percentages (max 5 questions per level per theme assumed)
      const max = 5;
      byLevel.committed = Math.min(Math.round((byLevel.committed / max) * 100), 100);
      byLevel.employer  = Math.min(Math.round((byLevel.employer  / max) * 100), 100);
      byLevel.leader    = Math.min(Math.round((byLevel.leader    / max) * 100), 100);
    }

    return { theme, ...byLevel };
  });
}

export default function MaturityChart({ assessments, levelScores }) {
  const data = buildThemeScores(assessments);

  return (
    <div className="bg-card border border-border rounded-xl p-3 sm:p-5 shadow-sm">
      <div className="mb-3 sm:mb-5">
        <h2 className="text-xs sm:text-sm font-semibold text-foreground">Maturity by Theme</h2>
        <p className="text-[10px] sm:text-xs text-muted-foreground mt-0.5">Scores across focus areas</p>
      </div>

      {/* Legend */}
      <div className="flex flex-wrap items-center gap-2 sm:gap-4 mb-3 sm:mb-5">
        {LEVELS.map(({ key, label, color }) => (
          <div key={key} className="flex items-center gap-1 sm:gap-1.5">
            <span className={`w-2 h-2 sm:w-3 sm:h-3 rounded-sm ${color}`} />
            <span className="text-[10px] sm:text-xs text-muted-foreground">{label}</span>
          </div>
        ))}
      </div>

      {/* Bars */}
      <div className="space-y-3 sm:space-y-4">
        {data.map(({ theme, committed, employer, leader }) => (
          <div key={theme}>
            <div className="flex items-center justify-between mb-1">
              <span className="text-[10px] sm:text-xs font-medium text-foreground">{theme}</span>
            </div>
            <div className="space-y-1">
              {[
                { label: 'Committed', value: committed, color: 'bg-slate-500' },
                { label: 'Employer',  value: employer,  color: 'bg-teal-500'  },
                { label: 'Leader',    value: leader,    color: 'bg-amber-500' },
              ].map(({ label, value, color }) => (
                <div key={label} className="flex items-center gap-1.5 sm:gap-2">
                  <span className="text-[9px] sm:text-[10px] text-muted-foreground w-16 sm:w-20 shrink-0 text-right">{label}</span>
                  <div className="flex-1 bg-muted rounded-full h-2 sm:h-2.5 overflow-hidden">
                    <div
                      className={`h-2 sm:h-2.5 rounded-full transition-all duration-500 ${color}`}
                      style={{ width: `${value}%` }}
                    />
                  </div>
                  <span className="text-[9px] sm:text-[10px] text-muted-foreground w-7 sm:w-8 shrink-0">{value}%</span>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}